import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { startNfcScan, NfcCancelled, NfcUserError, isNfcSupported } from './nfc'
import { nfcCheckin } from './api/attendance'
import { enqueueTag } from './offline/queue'
import { getSubdomain } from './org'
import { refreshPendingCount } from './offline/sync'

export type TagPhase = 'idle' | 'scanning' | 'sending'

export interface TagOutcome {
  /**
   * needs-device = 기기 미등록·미승인. 토스트만 띄우면 직원이 갈 곳을 모른다
   *
   * 🔴 needs-manual = NFC 를 못 쓰는 기기. **같은 이유로 따로 둔다** (2026-09-12).
   *   전에는 `error` 라 3초 토스트로 「수기 등록을 이용해 주세요」만 뜨고 사라졌다.
   *   말로는 가리키는데 갈 문이 없었다 — `needs-device` 에서 이미 한 번 푼 문제인데
   *   이쪽만 남아 있었다.
   */
  kind: 'saved' | 'queued' | 'cancelled' | 'error' | 'needs-device' | 'needs-manual'
  message: string
}

/**
 * NFC 태깅 한 번의 전 과정.
 *
 * 흐름: 스캔 → 전송. 전송이 네트워크 문제로 막히면 큐에 담는다.
 *
 * ★ 큐에 담는 시각은 **찍은 시각**이다 (clientTimestamp).
 *   나중에 전송돼도 그 시각이 기록된다 — 그게 이 제품의 존재 이유다.
 */
export function useTagging() {
  const qc = useQueryClient()
  const [phase, setPhase] = useState<TagPhase>('idle')
  const [outcome, setOutcome] = useState<TagOutcome | null>(null)

  async function tag(requestType: 'CHECKIN' | 'CHECKOUT'): Promise<void> {
    if (phase !== 'idle') return
    setOutcome(null)

    if (!isNfcSupported()) {
      // 갈 곳은 화면이 버튼으로 가리킨다 (Home 의 needs-manual 블록). 여기선 사실만 말한다
      setOutcome({ kind: 'needs-manual', message: '이 기기는 NFC 태그를 읽지 못합니다.' })
      return
    }

    // ── 1. 스캔 ──
    setPhase('scanning')
    let scan: Awaited<ReturnType<typeof startNfcScan>>
    try {
      scan = await startNfcScan()
    } catch (err) {
      setPhase('idle')
      if (err instanceof NfcCancelled) {
        setOutcome({ kind: 'cancelled', message: err.message })
        return
      }
      // 우리가 쓴 문장만 화면에 띄운다. 플랫폼 원문은 nfc.ts 가 이미 console 로 남겼다
      setOutcome({
        kind: 'error',
        message: err instanceof NfcUserError ? err.message : 'NFC 스캔에 실패했습니다. 다시 시도해 주세요.',
      })
      return
    }

    // 찍은 순간을 붙잡는다. 전송이 늦어져도 이 시각이 기록된다.
    const clientTimestamp = new Date().toISOString()
    const clientId = crypto.randomUUID()

    // ── 2. 전송 ──
    setPhase('sending')
    try {
      const res = await nfcCheckin({
        nfcUid: scan.nfcUid,
        requestType,
        nfcSignature: scan.nfcSignature,
        clientId,
        clientTimestamp,
      })

      if (res.success) {
        setPhase('idle')
        setOutcome({ kind: 'saved', message: requestType === 'CHECKIN' ? '출근이 기록되었습니다' : '퇴근이 기록되었습니다' })
        qc.invalidateQueries({ queryKey: ['attendance'] })
        return
      }

      // 서버가 명확히 거절한 것(4xx)은 큐에 넣어봐야 계속 거절당한다.
      const status = res.status ?? 0
      const permanent = status >= 400 && status < 500 && status !== 401 && status !== 429
      if (permanent) {
        setPhase('idle')
        // 403 = 기기 검증 실패(미등록·미승인·해제). 여기서 끝내면 직원은
        // "왜 안 되지" 만 남는다. 등록 화면으로 보낼 수 있게 따로 분류한다.
        setOutcome({
          kind: status === 403 ? 'needs-device' : 'error',
          message: res.error ?? '기록에 실패했습니다',
        })
        return
      }
      throw new Error(res.error ?? '전송 실패')
    } catch {
      // 네트워크·서버 문제 — 시각을 잃지 않도록 큐에 담는다
      await enqueueTag({
        clientId,
        nfcUid: scan.nfcUid,
        requestType,
        nfcSignature: scan.nfcSignature,
        clientTimestamp,
        queuedAt: new Date().toISOString(),
        // 어느 회사에서 찍었는지. 없으면 다른 회사 로그인 뒤 sync 가 이걸 버린다
        orgCode: getSubdomain(),
      })
      await refreshPendingCount()
      setPhase('idle')
      setOutcome({
        kind: 'queued',
        message: '지금은 전송하지 못했지만 찍은 시각으로 저장했습니다. 연결되면 자동으로 보냅니다.',
      })
    }
  }

  return { phase, outcome, tag, clearOutcome: () => setOutcome(null) }
}
